/**
 * Theme context providing light/dark/system mode toggling.
 * Reads the user's OS preference via `matchMedia` and applies
 * the selected theme class to the document root element.
 * @module ThemeContext
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

type ThemeMode = 'light' | 'dark' | 'system';

const THEME_MODES: Record<string, ThemeMode> = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

interface ThemeContextValue {
  darkMode: boolean;
  setDarkMode: (val: boolean) => void;
  toggleDarkMode: () => void;
  themePreference: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
  THEME_MODES: Record<string, ThemeMode>;
  isSystemTheme: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  // Theme preference: 'light', 'dark', or 'system'
  const [themePreference, setThemePreference] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('themePreference');
    if (saved && (Object.values(THEME_MODES) as string[]).includes(saved)) {
      return saved as ThemeMode;
    }
    // Default to system preference
    return THEME_MODES.SYSTEM;
  });

  // Actual dark mode state (computed from preference + system)
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('themePreference');
    if (saved === THEME_MODES.DARK) return true;
    if (saved === THEME_MODES.LIGHT) return false;
    // System preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Get system preference
  const getSystemPreference = useCallback(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, []);

  // Update dark mode based on preference
  const updateDarkMode = useCallback((preference: ThemeMode) => {
    if (preference === THEME_MODES.SYSTEM) {
      setDarkMode(getSystemPreference());
    } else {
      setDarkMode(preference === THEME_MODES.DARK);
    }
  }, [getSystemPreference]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user preference is 'system'
      if (themePreference === THEME_MODES.SYSTEM) {
        setDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [themePreference]);

  // Update when preference changes
  useEffect(() => {
    localStorage.setItem('themePreference', themePreference);
    updateDarkMode(themePreference);
  }, [themePreference, updateDarkMode]);

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);

  // Legacy toggle for backward compatibility
  const toggleDarkMode = () => {
    setThemePreference(prev => prev === THEME_MODES.DARK ? THEME_MODES.LIGHT : THEME_MODES.DARK);
  };

  // Set specific theme preference
  const setTheme = (mode: ThemeMode) => {
    if ((Object.values(THEME_MODES) as string[]).includes(mode)) {
      setThemePreference(mode);
    }
  };

  const value: ThemeContextValue = {
    darkMode,
    setDarkMode: (val: boolean) => setThemePreference(val ? THEME_MODES.DARK : THEME_MODES.LIGHT),
    toggleDarkMode,
    themePreference,
    setTheme,
    THEME_MODES,
    isSystemTheme: themePreference === THEME_MODES.SYSTEM,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
